import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import { FaCheckCircle, FaCrown, FaBoxOpen } from 'react-icons/fa';
import PaymentMethodsBlock from '../components/PaymentMethodsBlock';
import { buildWhatsAppUrl } from '../constants/whatsapp';
import './Planos.css';

const FORMATTER = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const ESSENTIAL_MONTHLY = 79.9;
const PREMIUM_MONTHLY = 99.9;
/** Anual = 10 × mensal (2 meses inclusos — mesmo desconto relativo nos dois planos). */
const ESSENTIAL_ANNUAL = ESSENTIAL_MONTHLY * 10;
const PREMIUM_ANNUAL = PREMIUM_MONTHLY * 10;

/** Assinatura quando o BTOne é adquirido com a Bientech (mensal mais baixo). */
const ESSENTIAL_BUNDLE_MONTHLY = 29.9;
const PREMIUM_BUNDLE_MONTHLY = 49.9;
const ESSENTIAL_BUNDLE_ANNUAL = ESSENTIAL_BUNDLE_MONTHLY * 10;
const PREMIUM_BUNDLE_ANNUAL = PREMIUM_BUNDLE_MONTHLY * 10;

const PlanCard = ({
  title,
  priceFormatted,
  periodSuffix,
  annualHint,
  extraPriceNote,
  description,
  features,
  highlight = false,
  bundleClass = '',
  ctaLabel,
  ctaHref,
  paymentBaseAmount,
  billingWord,
  paymentVariant,
}) => {
  return (
    <div className={`plan-card ${highlight ? 'highlight' : ''} ${bundleClass}`.trim()}>
      {highlight && (
        <div className="plan-badge">
          <FaCrown className="plan-badge-icon" />
          <span>Mais popular</span>
        </div>
      )}
      <div className="plan-header">
        <h3 className="plan-title">{title}</h3>
        <p className="plan-description">{description}</p>
      </div>

      <div className="plan-price">
        <div className="price-line">
          <span className="price-value">{priceFormatted}</span>
          <span className="price-period">{periodSuffix}</span>
          {annualHint ? (
            <span className="price-hint">{annualHint}</span>
          ) : null}
          {extraPriceNote ? (
            <span className="price-hint">{extraPriceNote}</span>
          ) : null}
        </div>
      </div>

      <ul className="plan-features">
        {features.map((featureText, index) => (
          <li key={index}>
            <FaCheckCircle className="feature-icon" />
            <span>{featureText}</span>
          </li>
        ))}
      </ul>

      <PaymentMethodsBlock
        baseAmount={paymentBaseAmount}
        variant={paymentVariant}
        billingWord={billingWord}
      />

      <div className="plan-cta">
        <a className="cta-button" href={ctaHref}>
          {ctaLabel}
        </a>
      </div>
    </div>
  );
};

const Planos = () => {
  const [billing, setBilling] = useState('monthly');

  const generateWhatsAppLink = (message) => buildWhatsAppUrl(message);

  const isAnnual = billing === 'annual';

  const essentialPrice = isAnnual
    ? FORMATTER.format(ESSENTIAL_ANNUAL)
    : FORMATTER.format(ESSENTIAL_MONTHLY);
  const premiumPrice = isAnnual
    ? FORMATTER.format(PREMIUM_ANNUAL)
    : FORMATTER.format(PREMIUM_MONTHLY);

  const essentialBundlePrice = isAnnual
    ? FORMATTER.format(ESSENTIAL_BUNDLE_ANNUAL)
    : FORMATTER.format(ESSENTIAL_BUNDLE_MONTHLY);
  const premiumBundlePrice = isAnnual
    ? FORMATTER.format(PREMIUM_BUNDLE_ANNUAL)
    : FORMATTER.format(PREMIUM_BUNDLE_MONTHLY);

  const periodSuffix = isAnnual ? 'por ano' : 'por mês';
  const annualHint = isAnnual ? 'Cobrança única anual — equivalente a 10 meses' : null;

  const messages = {
    essencialMonthly:
      'Olá! Tenho interesse no *Plano Essencial* (já possuo o BTOne) com cobrança *mensal* (R$ 79,90/mês). Gostaria de mais informações para assinar. Obrigado!',
    essencialAnnual:
      'Olá! Tenho interesse no *Plano Essencial* (já possuo o BTOne) com cobrança *anual* (R$ 799,00/ano). Gostaria de mais informações para assinar. Obrigado!',
    premiumMonthly:
      'Olá! Tenho interesse no *Plano Premium* (já possuo o BTOne) com cobrança *mensal* (R$ 99,90/mês). Gostaria de mais informações para assinar. Obrigado!',
    premiumAnnual:
      'Olá! Tenho interesse no *Plano Premium* (já possuo o BTOne) com cobrança *anual* (R$ 999,00/ano). Gostaria de mais informações para assinar. Obrigado!',
    essencialComEquipMonthly:
      'Olá! Tenho interesse no *Plano Essencial com compra do BTOne pela Bientech*, cobrança *mensal* da assinatura (R$ 29,90/mês). Quero combinar equipamento e ativação. Obrigado!',
    essencialComEquipAnnual:
      'Olá! Tenho interesse no *Plano Essencial com compra do BTOne pela Bientech*, cobrança *anual* da assinatura (R$ 299,00/ano). Quero combinar equipamento e ativação. Obrigado!',
    premiumComEquipMonthly:
      'Olá! Tenho interesse no *Plano Premium com compra do BTOne pela Bientech*, cobrança *mensal* da assinatura (R$ 49,90/mês). Quero combinar equipamento e ativação. Obrigado!',
    premiumComEquipAnnual:
      'Olá! Tenho interesse no *Plano Premium com compra do BTOne pela Bientech*, cobrança *anual* da assinatura (R$ 499,00/ano). Quero combinar equipamento e ativação. Obrigado!',
  };

  const waEssencial = generateWhatsAppLink(
    isAnnual ? messages.essencialAnnual : messages.essencialMonthly
  );
  const waPremium = generateWhatsAppLink(
    isAnnual ? messages.premiumAnnual : messages.premiumMonthly
  );
  const waEssencialComEquip = generateWhatsAppLink(
    isAnnual ? messages.essencialComEquipAnnual : messages.essencialComEquipMonthly
  );
  const waPremiumComEquip = generateWhatsAppLink(
    isAnnual ? messages.premiumComEquipAnnual : messages.premiumComEquipMonthly
  );

  const essentialPaymentBase = isAnnual ? ESSENTIAL_ANNUAL : ESSENTIAL_MONTHLY;
  const premiumPaymentBase = isAnnual ? PREMIUM_ANNUAL : PREMIUM_MONTHLY;
  const essentialBundlePaymentBase = isAnnual ? ESSENTIAL_BUNDLE_ANNUAL : ESSENTIAL_BUNDLE_MONTHLY;
  const premiumBundlePaymentBase = isAnnual ? PREMIUM_BUNDLE_ANNUAL : PREMIUM_BUNDLE_MONTHLY;
  const billingWord = isAnnual ? 'pagamento anual único' : 'mensalidade';

  return (
    <div className="planos-page">
      <section className="planos-hero">
        <Container>
          <h1>Planos de assinatura</h1>
          <p className="planos-hero-lead">
            Assinatura <strong>mensal ou anual</strong> para usar as soluções Bientech — aplicativo e portal web.
            Equipamentos (BTOne, BTTwo e acessórios) são à parte.
          </p>
          <div className="planos-hero-actions">
            <Link to="/produtos" className="planos-hero-cta planos-hero-cta--secondary">
              <FaBoxOpen aria-hidden />
              Ver equipamentos
            </Link>
          </div>

          <div className="billing-toggle-wrap">
            <div className="billing-toggle" role="group" aria-label="Mensal ou anual">
              <button
                type="button"
                className={`billing-toggle-btn ${billing === 'monthly' ? 'active' : ''}`}
                onClick={() => setBilling('monthly')}
              >
                Mensal
              </button>
              <button
                type="button"
                className={`billing-toggle-btn ${billing === 'annual' ? 'active' : ''}`}
                onClick={() => setBilling('annual')}
              >
                Anual
                <span className="billing-toggle-badge">Melhor valor</span>
              </button>
            </div>
          </div>
        </Container>
      </section>

      <Container className="planos-content">
        <section className="planos-pricing-section" aria-labelledby="planos-heading-tem-equipamento">
          <h2 id="planos-heading-tem-equipamento" className="planos-section-title">
            Já possuo o BTOne
          </h2>
          <p className="planos-section-lead">Para quem já tem o BTOne.</p>
          <Row className="pricing-grid pricing-grid--two align-items-start">
            <Col xs={12} lg={6} className="pricing-col">
              <PlanCard
                title="Plano Essencial"
                priceFormatted={essentialPrice}
                periodSuffix={periodSuffix}
                annualHint={annualHint}
                description="Ideal para começar ou com base menor de pacientes."
                features={[
                  'Acesso exclusivo ao App',
                  'Suporte para até 30 pacientes',
                  'Limite de 300 sessões',
                ]}
                ctaLabel="Assinar o Essencial"
                ctaHref={waEssencial}
                paymentBaseAmount={essentialPaymentBase}
                billingWord={billingWord}
                paymentVariant={isAnnual ? 'plan-annual' : 'plan-monthly'}
              />
            </Col>

            <Col xs={12} lg={6} className="pricing-col">
              <PlanCard
                title="Plano Premium"
                priceFormatted={premiumPrice}
                periodSuffix={periodSuffix}
                annualHint={annualHint}
                description="Para quem precisa de ferramentas completas e escala ilimitada."
                features={[
                  'Acesso ilimitado ao App e ao SaaS',
                  'Pacientes e sessões ilimitados',
                  'Portal web: clínicas, equipes e relatórios',
                ]}
                highlight
                ctaLabel="Assinar o Premium"
                ctaHref={waPremium}
                paymentBaseAmount={premiumPaymentBase}
                billingWord={billingWord}
                paymentVariant={isAnnual ? 'plan-annual' : 'plan-monthly'}
              />
            </Col>
          </Row>
        </section>

        <section className="planos-pricing-section" aria-labelledby="planos-heading-com-equipamento">
          <h2 id="planos-heading-com-equipamento" className="planos-section-title">
            Compra do BTOne com a Bientech
          </h2>
          <p className="planos-section-lead">Assinatura com compra do BTOne pela Bientech.</p>
          <Row className="pricing-grid pricing-grid--two align-items-start">
            <Col xs={12} lg={6} className="pricing-col">
              <PlanCard
                title="Plano Essencial + BTOne"
                priceFormatted={essentialBundlePrice}
                periodSuffix={periodSuffix}
                annualHint={annualHint}
                description="Ideal para começar ou com base menor de pacientes."
                features={[
                  'Acesso exclusivo ao App',
                  'Suporte para até 30 pacientes',
                  'Limite de 300 sessões',
                ]}
                bundleClass="plan-card--bundle"
                ctaLabel="Contratar Essencial + BTOne"
                ctaHref={waEssencialComEquip}
                paymentBaseAmount={essentialBundlePaymentBase}
                billingWord={billingWord}
                paymentVariant={isAnnual ? 'plan-annual' : 'plan-monthly'}
              />
            </Col>

            <Col xs={12} lg={6} className="pricing-col">
              <PlanCard
                title="Plano Premium + BTOne"
                priceFormatted={premiumBundlePrice}
                periodSuffix={periodSuffix}
                annualHint={annualHint}
                description="Para quem precisa de ferramentas completas e escala ilimitada."
                features={[
                  'Acesso ilimitado ao App e ao SaaS',
                  'Pacientes e sessões ilimitados',
                  'Portal web: clínicas, equipes e relatórios',
                ]}
                bundleClass="plan-card--bundle"
                ctaLabel="Contratar Premium + BTOne"
                ctaHref={waPremiumComEquip}
                paymentBaseAmount={premiumBundlePaymentBase}
                billingWord={billingWord}
                paymentVariant={isAnnual ? 'plan-annual' : 'plan-monthly'}
              />
            </Col>
          </Row>
        </section>
      </Container>
    </div>
  );
};

export default Planos;
